import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import i18n from '../../i18n';
import {
    MY_LIST_KEY_EVENT_OCCURRENCES,
    isPageIdSaved,
    saveOrRemoveId,
} from '../../utils/MyListStorage';
import { trackEvent } from '../../utils/datalayer';
import Button from '../Button';
import CardEvent from '../CardEvent';
import EventIcon from '../EventIcon/EventIcon';
import Icon from '../Icon';
import InfoBox from '../InfoBox';
import LabeledIcon from '../LabeledIcon';
import ListOccurrences from '../ListOccurrences';
import ShareModal from '../ShareModal';
import styles from './EventPageContent.module.scss';

const EventPageContent = ({
    occurrenceId,
    startTime,
    endTime,
    image = null,
    title = '',
    date = null,
    description = '',
    address = '',
    city,
    zipCode,
    callToActionUrl = '',
    callToActionLabel,
    externalWebsiteUrl = '',
    externalWebsiteUrlText = '',
    venueName,
    categories = [],
    occurrences = [],
    hasPassed = false,
    related,
    category,
    closestStation,
    isEventPreview = false,
}) => {
    const [openModal, setOpenModal] = useState(false);
    const [isSaved, setIsSaved] = useState();
    const [isClientSide, setIsClientSide] = useState(false);
    const [numberOfLines, setNumberOfLines] = useState(0);

    const titleRef = useRef();
    useEffect(() => {
        let lineNumberToStringMapper = {
            0: 'OneLine',
            1: 'OneLine',
            2: 'TwoLines',
            3: 'ThreeLines',
            4: 'FourLines',
        };
        if (titleRef.current) {
            const style = window.getComputedStyle(titleRef.current);
            const elementHeight = titleRef.current.offsetHeight;
            const lineHeight = parseInt(style.lineHeight, 10);
            const lines = elementHeight / lineHeight;
            setNumberOfLines(lineNumberToStringMapper[lines]);
        }
        // do the calculation again after rendering the fonts
        setTimeout(() => {
            const style = window.getComputedStyle(titleRef.current);
            const elementHeight = titleRef.current.offsetHeight;
            const lineHeight = parseInt(style.lineHeight, 10);
            const lines = elementHeight / lineHeight;
            setNumberOfLines(lineNumberToStringMapper[lines]);
        }, 100);
    }, []);

    let firstDate = null;
    let lastDate = null;
    let dateStr = null;

    if (
        occurrences.length > 0 &&
        occurrences[0]?.date &&
        occurrences[occurrences.length - 1]?.date
    ) {
        const firstDateObj = dayjs(occurrences[0]?.date);
        firstDate = firstDateObj.format('D MMM');
        const lastDateObj = dayjs(occurrences[occurrences.length - 1]?.date);
        dateStr = `${firstDate}`;
        if (!firstDateObj.isSame(lastDateObj)) {
            lastDate = lastDateObj.format('D MMM YYYY');
            dateStr = `${firstDate} — ${lastDate}`;
        }

        if (!!startTime && !!endTime) {
            dateStr += `, ${startTime
                .split(':')
                .slice(0, 2)
                .join(':')} – ${endTime.split(':').slice(0, 2).join(':')}`;
        }
    }

    useEffect(() => {
        setIsSaved(isPageIdSaved(MY_LIST_KEY_EVENT_OCCURRENCES, occurrenceId));
        setIsClientSide(true);
    }, []);

    const toggleShare = () => {
        setOpenModal(!openModal);
    };

    const handleSave = () => {
        let isSaved = saveOrRemoveId(
            MY_LIST_KEY_EVENT_OCCURRENCES,
            occurrenceId,
            title,
            categories
        );
        setIsSaved(isSaved);
    };

    const saveButtonText = isSaved
        ? i18n.t('generic.saved')
        : i18n.t('generic.save');

    const button = callToActionUrl
        ? {
              href: callToActionUrl,
              label: callToActionLabel || i18n.t('Event.callToActionText'),
          }
        : null;

    return (
        <article
            className={classNames(
                styles['EventPageContent'],
                styles[`EventPageContent--${numberOfLines}`]
            )}>
            <div className={styles['EventPageContent__Grid']}>
                <div className={styles['EventPageContent__Left']}>
                    <div className={styles['EventPageContent__TopLeft']}>
                        <div className={styles['EventPageContent__Category']}>
                            <span className="sr-only">
                                {i18n.t('generic.category')} :
                            </span>
                            <svg
                                aria-hidden={true}
                                xmlns="http://www.w3.org/2000/svg"
                                width="9"
                                height="12"
                                viewBox="0 0 9 12"
                                fill="none">
                                <path
                                    d="M9 11.5L9 4.44265L9 0.499998L9.53674e-07 11.5L9 11.5Z"
                                    fill="#6D2B9A"
                                />
                            </svg>
                            <span
                                className={
                                    styles['EventPageContent__EventLabel']
                                }>
                                {i18n.t('generic.event')}
                            </span>
                        </div>

                        <h1
                            ref={titleRef}
                            className={styles['EventPageContent__Title']}>
                            {title}
                        </h1>
                        <div className={styles['EventPageContent__PlaceDate']}>
                            {!!category?.title && (
                                <div
                                    className={
                                        styles[
                                            'EventPageContent__CategoryLabel'
                                        ]
                                    }>
                                    <EventIcon
                                        modifiers={['SmallMobile']}
                                        icon={category.icon}
                                    />
                                    {category.title}
                                </div>
                            )}
                            {!!venueName && (
                                <div
                                    className={
                                        styles['EventPageContent__Place']
                                    }>
                                    <Icon
                                        size="medium"
                                        type="place"
                                        color={'black800'}
                                    />
                                    {venueName}
                                </div>
                            )}
                            {!!dateStr && (
                                <div
                                    className={
                                        styles['EventPageContent__Date']
                                    }>
                                    <Icon
                                        size="medium"
                                        type="calender"
                                        color={'black800'}
                                    />
                                    {dateStr}
                                </div>
                            )}
                        </div>

                        {!!hasPassed && (
                            <div
                                className={
                                    styles['EventPageContent__TextWrapper']
                                }>
                                <p
                                    className={
                                        styles['EventPageContent__HasPassed']
                                    }>
                                    <Icon
                                        type={'error'}
                                        size={'fill'}
                                        color={'gray10'}
                                    />
                                    <strong
                                        className={
                                            styles[
                                                'EventPageContent__HasPassedText'
                                            ]
                                        }>
                                        {i18n.t('EventPageContent.hasPassed')}
                                    </strong>
                                </p>
                            </div>
                        )}

                        {image && image.renditions && (
                            <figure
                                className={
                                    styles['EventPageContent__ImageWrapper']
                                }>
                                <img
                                    className={
                                        styles['EventPageContent__Image']
                                    }
                                    sizes="(min-width: 1024px) 442px, 100vw"
                                    srcSet={`${image.renditions.large.src} 1200w,
                                    ${image.renditions.medium.src} 600w`}
                                    src={image.renditions.small.src}
                                    alt={`${image.alt}`}
                                    width={1020}
                                    height={510}
                                    style={{
                                        objectPosition:
                                            image.focal.x + ' ' + image.focal.y,
                                    }}
                                />

                                {image.caption && (
                                    <figcaption
                                        className={
                                            styles['EventPageContent__Caption']
                                        }>
                                        {i18n.t('generic.photo')}{' '}
                                        {image.caption}
                                    </figcaption>
                                )}
                            </figure>
                        )}
                    </div>

                    <div
                        className={styles['EventPageContent__Description']}
                        suppressHydrationWarning={true}
                        dangerouslySetInnerHTML={{ __html: description }}
                    />

                    <div className={styles['EventPageContent__ExtraInfo']}>
                        {!!occurrences.length && (
                            <div
                                className={
                                    styles['EventPageContent__Occurrences']
                                }>
                                <ListOccurrences occurrences={occurrences} />
                            </div>
                        )}

                        {(address || title || externalWebsiteUrl) && (
                            <div
                                className={
                                    styles['EventPageContent__Organizer']
                                }>
                                <InfoBox
                                    title={venueName}
                                    heading={i18n.t(
                                        'EventPageContent.gettingThere'
                                    )}
                                    address={address}
                                    city={city}
                                    zipCode={zipCode}
                                    href={externalWebsiteUrl}
                                    label={
                                        externalWebsiteUrlText ||
                                        externalWebsiteUrl
                                    }
                                    closestStation={closestStation}
                                    modifiers={['White', 'PinkBorder']}
                                />
                            </div>
                        )}
                    </div>

                    <div className={styles['EventPageContent__Functions']}>
                        <div
                            className={
                                styles['EventPageContent__LabeledIconWrapper']
                            }>
                            <LabeledIcon
                                type="share"
                                label={i18n.t('generic.share')}
                                color="black800"
                                isVertical={true}
                                labelMargin={
                                    styles['EventPageContent__LabelMargin']
                                }
                                onClick={toggleShare}
                            />
                        </div>

                        {isClientSide && (
                            <LabeledIcon
                                type="calender"
                                href={window.location.href + '/ical/'}
                                onClick={() =>
                                    trackEvent(
                                        'adCalendar',
                                        'Engagement',
                                        'Ad to calendar',
                                        window.location.href
                                    )
                                }
                                label={i18n.t('generic.add')}
                                color="black800"
                                isVertical={true}
                                labelMargin={
                                    styles['EventPageContent__LabelMargin']
                                }
                            />
                        )}
                        <Button
                            type="secondary"
                            icon="bookmark"
                            text={saveButtonText}
                            iconColor="black800"
                            iconSize="medium"
                            modifiers={[styles['EventPageContent__SaveButton']]}
                            isClicked={isSaved}
                            onClick={handleSave}
                        />
                    </div>
                </div>
                {!isEventPreview && (
                    <aside className={styles['EventPageContent__Right']}>
                        <h2 className={styles['EventPageContent__RightTitle']}>
                            {i18n.t('EventPageContent.lookingForMore')}
                        </h2>

                        <div className={styles['EventPageContent__RightCards']}>
                            {!!related?.length &&
                                related?.map((item, index) => {
                                    return (
                                        <CardEvent
                                            key={index}
                                            {...item}
                                            startDate={new Date(item.startDate)}
                                            endDate={new Date(item.endDate)}
                                            inSlider={true}
                                            modifiers={['White', 'RightCol']}
                                            isLookingForMore={true}
                                        />
                                    );
                                })}
                        </div>
                    </aside>
                )}
            </div>
            <ShareModal
                title={title}
                categories={categories}
                isOpen={openModal}
                handleClose={toggleShare}
            />
        </article>
    );
};

const EventPageContentOld = ({
    occurrenceId,
    startTime,
    endTime,
    image,
    title,
    date,
    description,
    address,
    callToActionUrl,
    callToActionLabel,
    externalWebsiteUrl,
    externalWebsiteUrlText,
    venueName,
    categories,
    occurrences,
    hasPassed,
}) => {
    const [openModal, setOpenModal] = useState(false);
    const [isSaved, setIsSaved] = useState();
    const [isClientSide, setIsClientSide] = useState(false);

    useEffect(() => {
        setIsSaved(isPageIdSaved(MY_LIST_KEY_EVENT_OCCURRENCES, occurrenceId));
        setIsClientSide(true);
    }, []);

    const toggleShare = () => {
        setOpenModal(!openModal);
    };

    const handleSave = () => {
        let isSaved = saveOrRemoveId(
            MY_LIST_KEY_EVENT_OCCURRENCES,
            occurrenceId,
            title,
            categories
        );
        setIsSaved(isSaved);
    };

    const saveButtonText = isSaved
        ? i18n.t('generic.saved')
        : i18n.t('generic.save');

    const button = callToActionUrl
        ? {
              href: callToActionUrl,
              label: callToActionLabel || i18n.t('Event.callToActionText'),
          }
        : null;

    return (
        <article className={styles['EventPageContent']}>
            <div className={styles['EventPageContent__Wrapper']}>
                <div className={styles['EventPageContent__Row']}>
                    {image && image.renditions && (
                        <figure
                            className={
                                styles['EventPageContent__ImageWrapper']
                            }>
                            <div
                                className={
                                    styles['EventPageContent__ImageContainer']
                                }>
                                <img
                                    className={
                                        styles['EventPageContent__Image']
                                    }
                                    sizes="(min-width: 1024px) 442px, 100vw"
                                    srcSet={`${image.renditions.large.src} 1200w,
                                    ${image.renditions.medium.src} 600w`}
                                    src={image.renditions.small.src}
                                    alt={`${image.alt}`}
                                    width={1020}
                                    height={510}
                                    style={{
                                        objectPosition:
                                            image.focal.x + ' ' + image.focal.y,
                                    }}
                                />
                            </div>

                            {image.caption && (
                                <figcaption
                                    className={
                                        styles['EventPageContent__Caption']
                                    }>
                                    {i18n.t('generic.photo')} {image.caption}
                                </figcaption>
                            )}
                        </figure>
                    )}

                    <section
                        className={styles['EventPageContent__TextWrapper']}>
                        {!!hasPassed && (
                            <p
                                className={
                                    styles['EventPageContent__HasPassed']
                                }>
                                <Icon
                                    type={'error'}
                                    size={'fill'}
                                    color={'gray10'}
                                />
                                <strong
                                    className={
                                        styles[
                                            'EventPageContent__HasPassedText'
                                        ]
                                    }>
                                    {i18n.t('EventPageContent.hasPassed')}
                                </strong>
                            </p>
                        )}
                    </section>
                </div>
            </div>
        </article>
    );
};

EventPageContent.propTypes = {
    image: PropTypes.object,
    title: PropTypes.string.isRequired,
    venueName: PropTypes.string,
    date: PropTypes.shape({ day: PropTypes.string, month: PropTypes.string }),
    time: PropTypes.string,
    description: PropTypes.string,
    href: PropTypes.string,
    address: PropTypes.string,
    occurrenceId: PropTypes.number,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    callToActionUrl: PropTypes.string,
    callToActionLabel: PropTypes.string,
    externalWebsiteUrl: PropTypes.string,
    externalWebsiteUrlText: PropTypes.string,
    categories: PropTypes.array,
    occurrences: PropTypes.array,
    hasPassed: PropTypes.bool,
};

export default EventPageContent;
